import Axios from "../../../lib/Axios";

/**
 * Support API Helper
 * 
 * API functions for support/complaints page
 */

/**
 * Get user complaints list
 * @param {Object} filters - Filter parameters
 * @param {string} filters.type - Type filter (Report bug, Suggest tag, Feedback, Help)
 * @param {string} filters.state - State filter (raised, inProgress, testing, done)
 * @param {string} filters.category - Category filter
 * @param {string} filters.dateColumn - Date column to filter on (createdAt or updatedAt)
 * @param {string} filters.dateRange - Date range filter (last_24_hours, last_7_days, last_30_days)
 * @param {string} filters.startDate - Start date (YYYY-MM-DD)
 * @param {string} filters.endDate - End date (YYYY-MM-DD)
 * @param {number} filters.limit - Pagination limit
 * @param {number} filters.page - Pagination page number
 * @returns {Promise} API response with complaints array and pagination
 */
export const getSupportList = async (filters = {}) => {
  const {
    type,
    state,
    category,
    dateColumn,
    dateRange,
    startDate,
    endDate,
    limit = 50,
    page = 1,
  } = filters;

  // Build query parameters object
  const params = {
    limit: limit.toString(),
    page: page.toString(),
  };
  
  if (type) params.type = type;
  if (state) params.state = state;
  if (category) params.category = category;
  if (dateColumn) params.dateColumn = dateColumn;
  if (dateRange && !startDate && !endDate) {
    params.dateRange = dateRange;
  }
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;

  console.log("API Params:", params);

  // GET /admin/user-complaints
  const result = await Axios.get("admin/user-complaints", { params });
  const complaints = result?.data?.result?.complaints || [];
  const pagination = result?.data?.result?.pagination || {};
  
  return {
    data: complaints,
    pagination: {
      currentPage: pagination.currentPage || page,
      limit: pagination.limit || limit,
      totalPages: pagination.totalPages || 1,
      totalRecords: pagination.totalRecords || 0,
    },
  };
};

/**
 * Get support/complaints statistics
 * @param {Object} filters - Filter parameters (same as getSupportList)
 * @param {string} filters.type - Type filter
 * @param {string} filters.state - State filter
 * @param {string} filters.category - Category filter
 * @param {string} filters.dateColumn - Date column to filter on (createdAt or updatedAt)
 * @param {string} filters.dateRange - Date range filter
 * @param {string} filters.startDate - Start date (YYYY-MM-DD)
 * @param {string} filters.endDate - End date (YYYY-MM-DD)
 * @returns {Promise} API response with statistics
 */
export const getSupportStats = async (filters = {}) => {
  const {
    type,
    state,
    category,
    dateColumn,
    dateRange,
    startDate,
    endDate,
  } = filters;

  // Build query parameters object
  const params = {};
  
  if (type) params.type = type;
  if (state) params.state = state;
  if (category) params.category = category;
  if (dateColumn) params.dateColumn = dateColumn;
  if (dateRange && !startDate && !endDate) {
    params.dateRange = dateRange;
  }
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;

  console.log("Stats API Params:", params);

  // GET /admin/user-complaints/stats
  const result = await Axios.get("admin/user-complaints/stats", { params });
  
  return result?.data?.result || {};
};

/**
 * Update user complaints (supports bulk update)
 * @param {Object} data - Update data
 * @param {Array<number>} data.complaint_id - Array of complaint IDs to update
 * @param {string} data.state - State to set (optional)
 * @param {string} data.category - Category to set (optional)
 * @param {string} data.sideNote - Side note text to set (optional)
 * @returns {Promise} API response with updated complaints
 */
export const updateComplaints = async (data) => {
  const { complaint_id, state, category, sideNote } = data;
  
  if (!complaint_id || !Array.isArray(complaint_id) || complaint_id.length === 0) {
    throw new Error("complaint_id must be a non-empty array");
  }

  const body = {
    complaint_id,
  };

  if (state) body.state = state;
  if (category) body.category = category;
  if (sideNote !== undefined && sideNote !== null) {
    body.sideNote = sideNote;
  }

  console.log("Update Complaints Body:", body);

  // PUT /admin/user-complaints
  const result = await Axios.put("admin/user-complaints", body);
  
  return result?.data?.result || {};
};

